import { LicenseDetails } from "@/components/license-details";
import { RegistrationDetailsSheetQuerySkeletons } from "@/components/license-details/registration-details-section-loading";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Button } from "@/components/ui/button";
import { CommentDeleteDialog } from "@/components/ui/comment-delete-dialog";
import ErrorComponent from "@/components/ui/error";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserType } from "@/constants";
import type {
  LicenseCommentsResponseSchema,
  LicensePaymentsResponseSchema,
  LicenseResponseSchema,
  NonRegistrantLicenseCommentsResponseSchema,
  NonRegistrantLicensePaymentsResponseSchema,
  RegistrantQuestionsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useCommentHandlers } from "@/hooks/use-comment-handlers";
import { useLicenseDetailsDisplayMode } from "@/hooks/use-license-details-display-mode";
import { cn, getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { Maximize2, PanelRight } from "lucide-react";

const EMPTY_COMMENTS: LicenseCommentsResponseSchema = { comments: [] };
const EMPTY_PAYMENTS: LicensePaymentsResponseSchema = { payments: [] };

interface LicenseDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isSelectedLicenseLoading: boolean;
  isAdditionalQuestionsQueryLoading: boolean;
  selectedLicenseError: unknown;
  additionalQuestionsQueryError: unknown;
  additionalQuestions?: RegistrantQuestionsResponseSchema;
  selectedLicenseData?: LicenseResponseSchema;
  commentsData?:
    | NonRegistrantLicenseCommentsResponseSchema
    | LicenseCommentsResponseSchema;
  isCommentsQueryLoading?: boolean;
  commentsQueryError?: unknown;
  paymentsData?:
    | NonRegistrantLicensePaymentsResponseSchema
    | LicensePaymentsResponseSchema;
  isPaymentsQueryLoading?: boolean;
  paymentsQueryError?: unknown;
  userType: UserType;
  /** Background recomputation of runtime totals after fast cached preview. */
  isLicenseRuntimeRefreshing?: boolean;
  initialFocusSection?: "property" | null;
  onInitialFocusSectionConsumed?: () => void;
}

export function LicenseDetailsSheet({
  isOpen,
  onOpenChange,
  isSelectedLicenseLoading,
  isAdditionalQuestionsQueryLoading,
  selectedLicenseError,
  additionalQuestionsQueryError: _additionalQuestionsQueryError,
  additionalQuestions,
  selectedLicenseData,
  commentsData,
  isCommentsQueryLoading = false,
  commentsQueryError: _commentsQueryError,
  paymentsData,
  isPaymentsQueryLoading = false,
  paymentsQueryError: _paymentsQueryError,
  userType,
  isLicenseRuntimeRefreshing = false,
  initialFocusSection = null,
  onInitialFocusSectionConsumed,
}: LicenseDetailsSheetProps) {
  const commentHandlers = useCommentHandlers({
    licenseId: selectedLicenseData?.id ?? "",
    userType,
  });
  const { displayMode, toggleDisplayMode } = useLicenseDetailsDisplayMode();
  const isFullscreen = displayMode === "fullscreen";

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        overlayClassName={isFullscreen ? undefined : "bg-transparent"}
        className={cn(
          "w-full overflow-hidden",
          isFullscreen ? "max-w-none border-l-0 sm:max-w-none" : "sm:max-w-xl",
        )}
      >
        <SheetHeader className="shrink-0 flex-row items-center gap-2 pr-12">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={toggleDisplayMode}
            aria-label={
              isFullscreen
                ? "Show license details in right pane"
                : "Show license details full screen"
            }
            title={isFullscreen ? "Right pane" : "Full screen"}
          >
            {isFullscreen ? <PanelRight /> : <Maximize2 />}
          </Button>
          <SheetTitle>License Details</SheetTitle>
          <SheetDescription className="hidden">
            View detailed information about your property registration including
            status, payments, and renewal information.
          </SheetDescription>
          <ContextHelpButton
            articleId="registration-details"
            className="ml-auto"
            helpPortal={
              userType === UserType.OMT_AMS
                ? "omt-ams"
                : userType === UserType.GOVERNMENT
                  ? "government"
                  : userType === UserType.OPERATOR
                    ? "operator"
                    : "registrant"
            }
          />
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
          {selectedLicenseError ? (
            <ErrorComponent
              title={getQueryErrorHeading(selectedLicenseError)}
              message={getQueryErrorMessage(selectedLicenseError)}
            />
          ) : !selectedLicenseData && isSelectedLicenseLoading ? (
            <RegistrationDetailsSheetQuerySkeletons />
          ) : selectedLicenseData ? (
            <LicenseDetails
              userType={userType}
              data={selectedLicenseData}
              additionalQuestions={additionalQuestions}
              commentsData={commentsData ?? EMPTY_COMMENTS}
              paymentsData={paymentsData ?? EMPTY_PAYMENTS}
              paymentsLoading={isPaymentsQueryLoading}
              commentsLoading={isCommentsQueryLoading}
              additionalQuestionsLoading={isAdditionalQuestionsQueryLoading}
              isLicenseRuntimeRefreshing={isLicenseRuntimeRefreshing}
              initialFocusSection={initialFocusSection}
              onInitialFocusSectionConsumed={onInitialFocusSectionConsumed}
              onCommentAdded={
                userType === UserType.REGISTRANT
                  ? undefined
                  : commentHandlers.handleAddComment
              }
              onRegistrantComment={
                userType === UserType.REGISTRANT
                  ? commentHandlers.handleRegistrantComment
                  : undefined
              }
              onFlagComplianceIssue={
                userType === UserType.OPERATOR ||
                userType === UserType.GOVERNMENT
                  ? commentHandlers.handleFlagComplianceIssue
                  : undefined
              }
              onClearRegistryNotification={
                userType === UserType.OPERATOR ||
                userType === UserType.GOVERNMENT
                  ? commentHandlers.handleClearRegistryNotification
                  : undefined
              }
              onClearComplianceIssue={
                userType === UserType.OPERATOR ||
                userType === UserType.GOVERNMENT
                  ? commentHandlers.handleClearComplianceIssue
                  : undefined
              }
              onCommentDeleted={commentHandlers.handleDeleteComment}
            />
          ) : null}
        </div>
      </SheetContent>

      {/* Comment delete confirmation dialog */}
      <CommentDeleteDialog
        isOpen={commentHandlers.isDeleteDialogOpen}
        onOpenChange={commentHandlers.setIsDeleteDialogOpen}
        onConfirm={commentHandlers.handleConfirmDelete}
      />
    </Sheet>
  );
}
